import { SmartFabDropdown } from "@/components/SmartFabDropdown";
import { UserLayout } from "@/components/user/layouts/UserLayout";
import type { KycRealtimeState } from "@/hooks/useKycRealtime";
import { signOut } from '@/lib/rpc/auth/session';
import { cn } from '@/lib/utils';
import { UX_LEXICON } from "@/lib/ux-lexicon";
import { DashboardState, LiquidationAddress, PayoutRoute, Transaction } from "@/types/dashboard";
import { ErrorState } from "@/ui/status/ErrorState";
import { semanticTokens } from '@packages/config/theme';
import { KycPendingBanner } from "./KycPendingBanner";
// DashboardHeader removed - Revolut-style: no top header, navigation via sidebar
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface DashboardLayoutProps {
  userId: string;
  customerId: string | null;
  bridgeCustomerId: string | null;
  kycStatus: string | null;
  tosStatus: string | null;
  kycRealtime?: KycRealtimeState;
  canCreateAddress: boolean;
  addresses: LiquidationAddress[];
  transactions: Transaction[];
  payoutRoutes: PayoutRoute[];
  dashboardState: DashboardState;
  onAddressCreated: () => void;
  onRefresh: () => void;
  onStatusRefresh: () => void;
  loading: boolean;
  statusRefreshKey: number;
  onAddBankAccount: () => void;
  onAddAddress: () => void;
  onboardingMode?: 'full' | 'receive_only' | null;
  error?: Error | string | null;
  children?: React.ReactNode;
  /** P0-1: KYC pending state from redirect */
  kycPending?: boolean;
  onKycPendingDismiss?: () => void;
  onKycPendingRefresh?: () => void;
  isKycRefreshing?: boolean;
}

/**
 * DashboardLayout — Layout wrapper for dashboard pages
 * 
 * CANON 5.3/5.4: This component is ONLY rendered when data is ready.
 * Dashboard.tsx handles the loading state and shows UnifiedDashboardSkeleton.
 * 
 * NO skeleton logic here — we receive ready data from parent.
 */
export function DashboardLayout({
  userId,
  customerId,
  bridgeCustomerId,
  kycStatus,
  tosStatus,
  kycRealtime,
  canCreateAddress,
  addresses,
  transactions,
  payoutRoutes,
  dashboardState,
  onAddressCreated,
  onRefresh,
  onStatusRefresh,
  loading,
  statusRefreshKey,
  onAddBankAccount,
  onAddAddress,
  onboardingMode,
  error,
  children,
  kycPending,
  onKycPendingDismiss,
  onKycPendingRefresh,
  isKycRefreshing
}: DashboardLayoutProps) {
  const navigate = useNavigate();

  const handleSignOut = useCallback(async () => {
    await signOut();
    const { clearUserPII } = await import("@/lib/appUser");
    clearUserPII();
    window.location.href = "/";
  }, []);

  const safeAddresses = Array.isArray(addresses) ? addresses : [];

  // Error state
  if (error) {
    return (
      <UserLayout
        sidebarProps={{
          canCreateAddress,
          onSignOut: handleSignOut,
          onNavigateHelp: () => navigate("/dashboard/help")
        }}
      >
        <ErrorState
          error={error}
          errorType="dashboard_failed"
          surface="dashboard"
          critical={true}
          onRetry={onRefresh}
          title={UX_LEXICON.dashboard.error.title}
          message={
            error instanceof Error
              ? UX_LEXICON.dashboard.error.message
              : typeof error === 'string'
                ? error
                : UX_LEXICON.dashboard.error.message
          }
          retryLabel={UX_LEXICON.dashboard.error.retry}
          className="m-6"
        />
      </UserLayout>
    );
  }

  // CANON 5.3/5.4: No skeleton here — Dashboard.tsx handles initial loading
  // This component only renders when data is ready

  return (
    <div data-testid="dashboard-loaded">
      <UserLayout
        sidebarProps={{
          canCreateAddress,
          onSignOut: handleSignOut,
          onNavigateHelp: () => navigate("/dashboard/help")
        }}
      >
        {/* P0-1: Show KYC pending banner when redirected from verification */}
        {kycPending && (
          <KycPendingBanner
            onRefresh={onKycPendingRefresh}
            onDismiss={onKycPendingDismiss}
            isRefreshing={isKycRefreshing}
          />
        )}
        <div className={cn("animate-fade-in", semanticTokens.motion.fadeIn)}>
          {children}
        </div>

        {/* Smart FAB for non-ACTIVE states */}
        {canCreateAddress && dashboardState !== "ACTIVE" && (
          <SmartFabDropdown
            state={dashboardState}
            onAddBankAccount={onAddBankAccount}
            onAddAddress={onAddAddress}
            onCopyAddress={() => {
              if (addresses[0]?.address) {
                navigator.clipboard.writeText(addresses[0].address);
              }
            }}
            latestAddress={addresses[0]?.address}
          />
        )}
      </UserLayout>
    </div>
  );
}
